import React, { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import DropToggle from 'components/DropToggle';
import Icon from 'components/commons/Icon';
import { useItemsToShow } from 'hooks';
import { DEFAULT_SOURCE_CURRENCY } from 'utils';
import { getMbArchiveTableRows } from 'utils/mbArchive';

import {
  Wrapper,
  Title,
  TableWrapper,
  Table,
  TableHead,
  HeadRow,
  HeadCell,
  BodyRow,
  LabelCell,
  RateCell,
  RateValue,
  RateContent,
  TrendIcon,
} from './styles';

const RANGE_ROWS_COLLAPSED_COUNT = 8;

const MbArchiveTable = () => {
  const { currency = DEFAULT_SOURCE_CURRENCY } = useParams();
  const { formatMessage, formatNumber } = useIntl();
  const mbArchive = useSelector(state => state.currency?.mbArchive);

  const currentCurrency = useSelector(state => {
    const list = state.currency?.currencyList || [];

    return (
      list.find(item => item?.name?.toLowerCase() === currency.toLowerCase()) ||
      null
    );
  });

  const currencyLabel =
    currentCurrency?.nameG || currentCurrency?.name || currency.toUpperCase();

  const isRangeMode = mbArchive?.mode === 'range';
  const items = mbArchive?.data?.[currency?.toUpperCase()] || [];

  const rows = useMemo(() => getMbArchiveTableRows(items, mbArchive?.mode), [
    items,
    mbArchive?.mode,
  ]);

  const collapsedCount = isRangeMode ? RANGE_ROWS_COLLAPSED_COUNT : rows.length;
  const { isShow, toggle, items: visibleRows } = useItemsToShow(
    rows,
    collapsedCount
  );
  const isCollapsible = isRangeMode && rows.length > RANGE_ROWS_COLLAPSED_COUNT;

  useEffect(() => {
    toggle(false);
  }, [currency, mbArchive?.date, mbArchive?.dateTo]);

  const formatRate = useCallback(
    value =>
      formatNumber(Number(value), {
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      }),
    [formatNumber]
  );

  const firstColumnTitle = formatMessage({
    id: isRangeMode
      ? 'mbArchivePage.tradeHistory.date'
      : 'mbArchivePage.tradeHistory.time',
  });
  const purchaseTitle = formatMessage({
    id: 'mbArchivePage.tradeHistory.purchase',
  });
  const saleTitle = formatMessage({ id: 'mbArchivePage.tradeHistory.sale' });

  const renderRateCell = (price, trend) => {
    const isDiffEmpty = trend === '-' || trend == null;
    const convertedDiff = isDiffEmpty ? 0 : Number(trend);

    return (
      <RateContent>
        <RateValue>{formatRate(price)}</RateValue>
        {!isDiffEmpty && convertedDiff !== 0 && (
          <TrendIcon $fall={convertedDiff < 0}>
            <Icon name={convertedDiff < 0 ? 'polygon' : 'growth'} />
          </TrendIcon>
        )}
      </RateContent>
    );
  };

  if (!rows.length) {
    return null;
  }

  return (
    <Wrapper>
      <Title>
        <FormattedMessage
          id="mbArchivePage.tradeHistory.title"
          values={{ currency: currencyLabel }}
        />
      </Title>

      <TableWrapper>
        <Table>
          <TableHead>
            <HeadRow>
              <HeadCell>{firstColumnTitle}</HeadCell>
              <HeadCell>{purchaseTitle}</HeadCell>
              <HeadCell>{saleTitle}</HeadCell>
            </HeadRow>
          </TableHead>
          <tbody>
            {visibleRows.map((row, index) => (
              <BodyRow key={row.key} $isEven={index % 2 === 1}>
                <LabelCell>{row.label}</LabelCell>
                <RateCell>{renderRateCell(row.bid, row.trendBid)}</RateCell>
                <RateCell>{renderRateCell(row.ask, row.trendAsk)}</RateCell>
              </BodyRow>
            ))}
          </tbody>
        </Table>
      </TableWrapper>

      {isCollapsible && (
        <DropToggle
          onPress={toggle}
          isOpen={isShow}
          openTitle="show.all.button"
          closeTitle="hide.button"
        />
      )}
    </Wrapper>
  );
};

export default MbArchiveTable;
